import parseEncodedJSON from "@/lib/parseEncodedJSON";
import { BaseAttrsSchema, ConfigPartSchema } from "@/types";
import {
	BaseFundExplorerConfigSchema,
	LocalIDSchema,
} from "hsl-core/FundExplorer";
import { API, EntityKeySchema, ScalarValueSchema } from "hsl-core/FundExplorer";
import { createDynamicZodSchema } from "hsl-core/utils";
import { z } from "zod";
import { breadcrumbs } from "./components/Layout/Layout.css";

export const FundExplorerAttrsSchema = BaseAttrsSchema.extend({
	apiId: z.coerce.number(),
	configId: z.coerce.number(),
	languageId: z.coerce.number().optional(),
	action: z.string().optional(),
});

export type FundExplorerAttrs = z.infer<typeof FundExplorerAttrsSchema>;

export type SortOrder = "asc" | "desc" | null;
export type AppliedSort = Record<string, SortOrder>;

export const MainColSchema = z.object({
	label: z.string(),
	key: EntityKeySchema,
	isSortable: z.boolean().optional(),
	groupName: z.string().optional(),
	isPrimary: z.boolean().optional(),
	// gridWidth: z.string()
});

export const CardDisplayRowSchema = z.tuple([z.string(), EntityKeySchema]);
export const CardDisplaySchema = CardDisplayRowSchema.array();
export const CardDisplayConfigSchema = CardDisplaySchema.array();

export type CardDisplayConfig = z.infer<typeof CardDisplayConfigSchema>;

export const BreadcrumbSchema = z.object({
	label: z.string(),
	path: z.string().optional(),
});
export type Breadcrumb = z.infer<typeof BreadcrumbSchema>;

export const RLAMFundExplorerConfigSchema = BaseFundExplorerConfigSchema.extend(
	{
		mainCols: MainColSchema.array(),
		flagConfigId: z.coerce.number().optional(),
		cityWireConfigId: z.coerce.number().optional(),
		rowConfigId: z.coerce.number().optional(),
		groupBy: EntityKeySchema.array().optional(),
		cardDisplay: CardDisplayConfigSchema.optional(),
		sort: z
			.record(EntityKeySchema, z.enum(["asc", "desc"]).nullable())
			.optional(),
		enableFundBadges: z.boolean().default(false),
		breadcrumbs: BreadcrumbSchema.array().optional(),
		disclaimerId: z.number().optional(),
	},
);
export type RLAMFundExplorerConfig = z.infer<
	typeof RLAMFundExplorerConfigSchema
>;

export const BaseDatumProperties = ["id", "localId"] as const;
export const ShareClassProperties = [
	...BaseDatumProperties,
	"__feCrown",
	"__incomeType",
	"__morningstarMedalist",
	"__shareClassCurrency",
	"__shareClassIsin",
	"__shareClassLetter",
	"__shareClassSedolsedol",
	"feCrown",
	"incomeType",
	"morningstarMedalist",
	"shareClassCurrency",
	"shareClassIsin",
	"shareClassLetter",
	"shareClassSedolsedol",
] as const;
export const FundProperties = [
	...BaseDatumProperties,
	"__abiSector",
	"__advisercentreRecommended",
	"__altURL",
	"__instURL",
	"__baseCurrency",
	"__benchmarkName",
	"__capability",
	"__eliteRated",
	"__focusFundUKIntermediary",
	"__fundLaunchDate",
	"__fundName",
	"__fundPageURL",
	"__fundSize__date",
	"__fundSize",
	"__fundType",
	"__morningstarCategory",
	"__region",
	"__rsmrRated",
	"__rsmrResponsible",
	"__sector",
	"__tagLine",
	"abiSector",
	"advisercentreRecommended",
	"altURL",
	"instURL",
	"baseCurrency",
	"benchmarkName",
	"capability",
	"eliteRated",
	"focusFundUKIntermediary",
	"fundLaunchDate",
	"fundName",
	"fundPageURL",
	"fundSize__date",
	"fundSize",
	"fundType",
	"morningstarCategory",
	"region",
	"rsmrRated",
	"rsmrResponsible",
	"sector",
	"tagLine",
	"fund_badge_info",
] as const;

export const ProfileProperties = [
	...BaseDatumProperties,
	"__citywireRating",
	"__firstName",
	"__headshot",
	"__lastName",
	"__person",
	"__title",
	"citywireRating",
	"firstName",
	"headshot",
	"lastName",
	"person",
	"title",
] as const;

export const DictValue = z.record(z.string(), ScalarValueSchema);
export const DatumValue = ScalarValueSchema.or(ScalarValueSchema.array())
	.or(DictValue)
	.nullable();

export const ShareClassDatumSchema = createDynamicZodSchema(
	ShareClassProperties,
	DatumValue,
).partial();
export type ShareClassDatum = z.infer<typeof ShareClassDatumSchema>;

export const FundDatumSchema = createDynamicZodSchema(
	FundProperties,
	DatumValue,
)
	.partial()
	.extend({
		currency: API.CurrencySchema.optional(),
		shareclasses: ShareClassDatumSchema.array(),
	});
export type FundDatum = z.infer<typeof FundDatumSchema>;

export const ProfileDatumSchema = createDynamicZodSchema(
	ProfileProperties,
	DatumValue,
).extend({
	funds: LocalIDSchema.array(),
});
export type ProfileDatum = z.infer<typeof ProfileDatumSchema>;

export const PersonSchema = ProfileDatumSchema.extend({
	id: z.string(),
	profiles: LocalIDSchema.array(),
	funds: LocalIDSchema.array(),
}).omit({
	localId: true,
});
export type Person = z.infer<typeof PersonSchema>;

export const EncodedJSONSchema = z
	.string()
	.transform<Record<string, unknown>>((val, ctx) => {
		try {
			return parseEncodedJSON(val);
		} catch (e) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Invalid JSON",
			});
			return z.NEVER;
		}
	});

export const FundExplorerTabConfigSchema = ConfigPartSchema.extend({
	label: z.string(),
}).or(z.object({}));
export type FundExplorerTabConfig = z.infer<typeof FundExplorerTabConfigSchema>;

const fundExplorerTabKeys = [
	"profile",
	"overview",
	"shareclassesPrices",
	"performance",
	"documents",
] as const;
export const FundExplorerPanelConfigSchema = createDynamicZodSchema(
	fundExplorerTabKeys,
	FundExplorerTabConfigSchema,
);
export type FundExplorerPanelConfig = z.infer<
	typeof FundExplorerPanelConfigSchema
>;
